import { useTranslation } from "react-i18next";
import React, { useState, useMemo, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ShoppingCart, X } from "lucide-react";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Layout } from "@/components/layout";

import { QuickStartSection } from "@/components/orders/QuickStartSection";
import { ProductCatalog } from "@/components/products/ProductCatalog";
import { CartPanel } from "@/components/orders/CartPanel";
import { MobileCartBar } from "@/components/orders/MobileCartBar";
import { CartItemList } from "@/components/orders/CartItemList";
import { RecommendationChips } from "@/components/orders/RecommendationChips";
import { BuyAgainSection } from "@/components/orders/BuyAgainSection";
import { useBuyAgain } from "@/hooks/use-buy-again";
import { CheckoutForm } from "@/components/orders/CheckoutForm";
import type { CheckoutFormData, CheckoutFormRef } from "@/components/orders/CheckoutForm";
import { OrderSummaryPanel } from "@/components/orders/OrderSummaryPanel";
import { OrderConfirmation } from "@/components/orders/OrderConfirmation";
import { SaveTemplateDialog } from "@/components/orders/SaveTemplateDialog";
import type { SelectedProduct, ProductSelection } from "@/types/product";
import { useShoppingCart } from "@/hooks/use-shopping-cart";
import { useOrderSubmission } from "@/hooks/use-order-submission";
import { useTemplateManagement } from "@/hooks/use-template-management";
import { useProductSummary } from "@/hooks/use-product-summary";
import { formatCentsAsDollars } from "@/lib/price-utils";
import { useLocation } from "wouter";
import { useProductEvents } from "@/hooks/use-product-events";


type OrderStep = "shopping" | "checkout" | "confirmation";
type OrderFlowMode = "create" | "edit" | "view";


export interface EditOrderData {
  id: string;
  title: string;
  description: string;
  fulfillmentType: "delivery" | "pickup";
  notes: string;
  products: SelectedProduct[];
  orderStatus?: string;
}

interface NewOrderFlowProps {
  onBack: () => void;
  templateId?: string;
  productId?: string;
  clearTemplateParam?: () => void;
  mode?: OrderFlowMode;
  editOrder?: EditOrderData;
  /** Banner rendered above the step content, persistent across steps
   *  (e.g. the PC "Ordering for" contractor switcher). */
  contextBanner?: React.ReactNode;
  /** Hide the save-as-draft actions (PC flow: PCs have no drafts list, and a
   *  PC-saved draft would surface in the contractor's orders unannounced). */
  hideSaveDraft?: boolean;
  /** Called by "Start New Order" on the confirmation screen. When set, it
   *  replaces the in-place reset (the edit page uses it to leave the old order's URL). */
  onStartNewOrder?: () => void;
  /** Called after an order is submitted successfully. */
  onOrderSubmitted?: () => void;
}

export const NewOrderFlow: React.FC<NewOrderFlowProps> = ({
  onBack,
  templateId,
  productId,
  clearTemplateParam,
  mode = "create",
  editOrder,
  contextBanner,
  hideSaveDraft = false,
  onStartNewOrder,
  onOrderSubmitted,
}) => {
  const { t } = useTranslation();
  const isReadOnly = mode === "view";
  const isEditMode = mode === "edit";

  const { capture } = useProductEvents({ component: "NewOrderFlow" });

  // --- Step state ---
  const [step, setStep] = useState<OrderStep>("shopping");
  const [cartDrawerOpen, setCartDrawerOpen] = useState(false);
  const checkoutFormRef = useRef<CheckoutFormRef>(null);

  // --- Cart ---
  // In create mode: loads the user's persistent shopping cart, saves on change (debounced).
  // In edit/view mode: disabled, seeded with the order's products.
  const cart = useShoppingCart({
    initialProducts: mode !== "create" ? editOrder?.products : undefined,
    disabled: mode !== "create",
  });

  // --- Templates (only in create mode) ---
  // `cartReady` gates URL-based template/product auto-application so it only
  // fires after the persistent cart finishes hydrating. Without this gate,
  // a returning user with a non-empty cart visiting ?template=X would have
  // their cart silently wiped before the confirmation dialog could appear.
  const templateMgmt = useTemplateManagement({
    templateId: mode === "create" ? templateId : undefined,
    productId: mode === "create" ? productId : undefined,
    clearTemplateParam,
    cartItemCount: cart.selectedProducts.length,
    cartReady: !cart.isLoading,
    onApplyProducts: cart.setSelectedProducts,
  });

  // --- Submission ---
  // In edit mode: submit patches the existing order. In create mode: submit patches
  // the cart draft (which then gets isCart flipped to false server-side).
  const submission = useOrderSubmission({
    existingOrderId: editOrder?.id || cart.cartOrderId || undefined,
    selectedTemplateId: templateMgmt.getLastUsedTemplateId(),
    onTrackTemplateUsage: templateMgmt.trackTemplateUsage,
    onFallbackNavigate: onBack,
    onAfterSubmit: cart.invalidateCart,
  });

  // --- Buy Again ---
  const { items: buyAgainItems } = useBuyAgain();

  // --- Derived data ---
  const cartProductSelections: ProductSelection[] = useMemo(() => {
    return cart.selectedProducts.map((sp) => ({
      productId: sp.uid || "",
      quantity: sp.quantity,
      unitPrice: sp.unitPrice,
      notes: sp.notes,
      description: sp.description ?? undefined,
      hajocaProductId: sp.hajocaProductId ?? undefined,
    }));
  }, [cart.selectedProducts]);

  const cartSummary = useProductSummary({
    selectedProducts: cart.selectedProducts.map((sp) => ({
      quantity: sp.quantity,
      unitPrice: sp.unitPrice,
    })),
    showPricing: true,
  });

  // --- Step handlers ---
  const handleGoToCheckout = useCallback(() => {
    if (cart.selectedProducts.length === 0) return;
    setStep("checkout");
  }, [cart.selectedProducts.length]);

  const handleBackToShopping = useCallback(() => {
    setStep("shopping");
  }, []);

  const handlePlaceOrder = useCallback(
    async (checkoutData: CheckoutFormData) => {
      capture({
        event: "order_submit_started",
        context: {
          productCount: cart.selectedProducts.length,
          mode: isEditMode ? "edit" : "create",
        },
      });
      const result = await submission.placeOrder(checkoutData, cart.selectedProducts);
      if (result) {
        cart.invalidateCart();
        setStep("confirmation");
        // Let the page react to the submit (e.g. update the URL)
        onOrderSubmitted?.();
      } else {
        capture({
          event: "order_submit_failed",
          context: {
            productCount: cart.selectedProducts.length,
            mode: isEditMode ? "edit" : "create",
            error: "submission_failed",
          },
        });
      }
    },
    [submission, cart, capture, isEditMode, onOrderSubmitted],
  );

  const handleRemoveProduct = useCallback(
    (productId: string) => {
      capture({ event: "product_removed_from_order", context: { productId } });
      cart.removeProduct(productId);
    },
    [capture, cart],
  );

  const handleSaveDraft = useCallback(
    async (checkoutData: CheckoutFormData) => {
      const result = await submission.saveDraft(checkoutData, cart.selectedProducts);
      if (result) {
        cart.invalidateCart();
        onBack();
      }
    },
    [submission, cart, onBack],
  );


  const handleStartNewOrder = useCallback(() => {
    // Hand off to the page when it provides its own "Start New Order"
    if (onStartNewOrder) {
      onStartNewOrder();
      return;
    }
    // Otherwise reset this flow in place
    setStep("shopping");
    cart.clearAll();
    submission.setConfirmedOrder(null);
  }, [cart, submission, onStartNewOrder]);

  // --- Breadcrumb ---
  const breadcrumbTitle = mode === "view"
    ? (editOrder?.title || t("components.orders.newOrderFlow.viewOrder"))
    : mode === "edit"
      ? (editOrder?.title || t("components.orders.newOrderFlow.editOrder"))
      : t("components.orders.newOrderFlow.newOrder");

  const breadcrumb = (
    <div className="flex items-center gap-2 text-sm">
      <button onClick={onBack} className="text-muted-foreground hover:text-foreground transition-colors">
        {t("common.orders")}
      </button>
      <span className="text-muted-foreground">/</span>
      <span className="font-semibold">{breadcrumbTitle}</span>
    </div>
  );

  return (
    <Layout fillViewport headerContent={breadcrumb}>
      <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
        {/* On the shopping step the banner lives inside the catalog column so
            it doesn't span the cart and the cart keeps full height. */}
        {!(step === "shopping" && !isReadOnly) && contextBanner}
        {/* View mode: read-only order summary */}
        {step === "shopping" && isReadOnly && (
          <ViewModeContent
            editOrder={editOrder}
            cartProductSelections={cartProductSelections}
            onBack={onBack}
          />
        )}

        {/* Shopping step (create/edit) */}
        {step === "shopping" && !isReadOnly && (
          <div className="flex gap-6 mt-4 flex-1 min-h-0">
            <div className={`flex-1 min-w-0 overflow-y-auto overflow-x-hidden space-y-4 lg:pr-2 lg:pb-4 ${cart.selectedProducts.length > 0 ? "pb-20" : "pb-4"}`}>
              {contextBanner}
              {!isEditMode && (
                <BuyAgainSection
                  items={buyAgainItems}
                  onProductSelect={cart.selectProduct}
                  selectedProducts={cart.selectedProducts.map((sp) => ({
                    productId: sp.uid || "",
                    quantity: sp.quantity,
                    unitPrice: sp.unitPrice,
                  }))}
                />
              )}
              {!isEditMode && (
                <QuickStartSection
                  templates={templateMgmt.templates}
                  templatesLoading={templateMgmt.templatesLoading}
                  templatesError={templateMgmt.templatesError}
                  onSelectTemplate={templateMgmt.selectTemplate}
                />
              )}
              {cart.selectedProducts.length > 0 && !isEditMode && (
                <div className="lg:hidden">
                  <RecommendationChips
                    cartProductIds={cart.selectedProducts.map((sp) => sp.uid || "")}
                    onAddProduct={cart.addProduct}
                  />
                </div>
              )}
              <ProductCatalog
                selectedProducts={cart.selectedProducts.map((sp) => ({
                  productId: sp.uid || "",
                  quantity: sp.quantity,
                  unitPrice: sp.unitPrice,
                }))}
                onProductSelect={cart.selectProduct}
                showCachedPricing
              />
            </div>

            <div className="hidden lg:flex lg:w-[360px] flex-shrink-0 flex-col min-h-0">
              <CartPanel
                selectedProducts={cartProductSelections}
                onProductQuantityChange={cart.changeQuantity}
                onProductRemove={handleRemoveProduct}
                onClearAll={cart.clearAll}
                onAddProduct={isEditMode ? undefined : cart.addProduct}
                onCheckout={handleGoToCheckout}
                showPricing
              />
            </div>
          </div>
        )}

        {/* Checkout step */}
        {step === "checkout" && (
          <div className="flex-1 min-h-0 overflow-y-auto pb-20 lg:pb-4 mt-4">
            {/* Back link at top */}
            <div className="hidden lg:block mb-4">
              <button
                onClick={handleBackToShopping}
                disabled={submission.isSubmitting}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                {t("components.orders.newOrderFlow.backToCart")}
              </button>
            </div>

            {/* 2-column layout on desktop; single column on mobile */}
            <div className="flex flex-col lg:grid lg:grid-cols-[1fr_360px] gap-6">
              {/* Left column: order items + recommendations */}
              <div className="space-y-6 min-w-0">
                <OrderSummaryPanel selectedProducts={cartProductSelections} showPricing />
                {/* Mobile-only: Save as Template sits under the item review — a
                    natural "remember these items" spot. Desktop has it in the
                    action stack alongside Place Order / Save as Draft. */}
                <Button
                  variant="outline"
                  onClick={() => templateMgmt.setShowSaveTemplateDialog(true)}
                  disabled={cart.selectedProducts.length === 0 || submission.isSubmitting}
                  className="w-full lg:hidden"
                >
                  Save as Template
                </Button>
                {cart.selectedProducts.length > 0 && (
                  <RecommendationChips
                    cartProductIds={cart.selectedProducts.map((sp) => sp.uid || "")}
                    onAddProduct={cart.addProduct}
                  />
                )}
              </div>

              {/* Right column: checkout form + actions.
                  Form renders on all sizes (needs to be in the DOM for ref).
                  Action buttons only on desktop (mobile uses the pinned bottom bar). */}
              <div className="lg:sticky lg:top-4 lg:self-start space-y-4">
                <Card>
                  <CardContent className="pt-6 space-y-4">
                    <div className="space-y-1 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">
                          {cartSummary.totalItems} {cartSummary.totalItems === 1 ? "item" : "items"}
                        </span>
                        {cartSummary.totalValueCents > 0 && (
                          <span className="text-lg font-semibold">
                            ${formatCentsAsDollars(cartSummary.totalValueCents)}
                            {cartSummary.hasMissingPrices ? "+" : ""}
                          </span>
                        )}
                      </div>
                      {cartSummary.totalValueCents > 0 && (
                        <p className="text-xs text-muted-foreground text-right">
                          {t("components.orders.newOrderFlow.estimateExcludesTax")}
                        </p>
                      )}
                    </div>

                    <CheckoutForm
                      ref={checkoutFormRef}
                      suggestedTitle={isEditMode ? (editOrder?.title || "") : `PO-${cart.selectedProducts.length}-items`}
                      initialData={isEditMode && editOrder ? {
                        title: editOrder.title,
                        fulfillmentType: editOrder.fulfillmentType,
                        notes: editOrder.notes,
                      } : undefined}
                      onSubmit={handlePlaceOrder}
                      onSaveDraft={handleSaveDraft}
                      disabled={submission.isSubmitting}
                    />

                    {/* Desktop-only action stack. Mobile actions live in MobileCartBar. */}
                    <div className="hidden lg:block space-y-2">
                      <Button
                        onClick={() => checkoutFormRef.current?.submit()}
                        disabled={submission.isSubmitting}
                        className="w-full"
                        size="lg"
                      >
                        {submission.isSubmitting
                          ? (isEditMode ? t("common.saving") : t("components.orders.newOrderFlow.placingOrder"))
                          : (isEditMode ? t("components.orders.newOrderFlow.saveSubmit") : t("components.orders.newOrderFlow.placeOrder"))}
                      </Button>

                      {!hideSaveDraft && (
                        <Button
                          variant="outline"
                          onClick={() => checkoutFormRef.current?.saveDraft()}
                          disabled={submission.isSubmitting}
                          className="w-full"
                        >
                          {t("components.orders.newOrderFlow.saveAsDraft")}
                        </Button>
                      )}

                      <Button
                        variant="outline"
                        onClick={() => templateMgmt.setShowSaveTemplateDialog(true)}
                        disabled={cart.selectedProducts.length === 0 || submission.isSubmitting}
                        className="w-full"
                      >
                        {t("components.orders.newOrderFlow.saveAsTemplate")}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        )}

        {/* Confirmation step */}
        {step === "confirmation" && submission.confirmedOrder && (
          <div className="flex-1 min-h-0 overflow-y-auto">
            <OrderConfirmation
              orderId={submission.confirmedOrder.id}
              orderNumber={submission.confirmedOrder.orderNumber}
              poNumber={submission.confirmedOrder.poNumber}
              hajocaOrderId={submission.confirmedOrder.hajocaOrderId}
              orderConfirmation={submission.confirmedOrder.orderConfirmation}
              fulfillmentType={submission.confirmedOrder.fulfillmentType}
              itemCount={submission.confirmedOrder.itemCount}
              totalCost={submission.confirmedOrder.totalCost}
              onStartNewOrder={handleStartNewOrder}
              onSaveAsTemplate={() => templateMgmt.setShowSaveTemplateDialog(true)}
            />
          </div>
        )}
      </div>

      {/* Mobile bottom bar (hidden in view mode) */}
      {step === "shopping" && !isReadOnly && (
        <MobileCartBar
          selectedProducts={cartProductSelections}
          onViewCart={() => setCartDrawerOpen(true)}
          onCheckout={handleGoToCheckout}
        />
      )}
      {step === "checkout" && (
        <MobileCartBar
          selectedProducts={cartProductSelections}
          onBack={handleBackToShopping}
          onPlaceOrder={() => checkoutFormRef.current?.submit()}
          onSaveDraft={
            hideSaveDraft ? undefined : () => checkoutFormRef.current?.saveDraft()
          }
          isSubmitting={submission.isSubmitting}
        />
      )}

      {/* Mobile cart drawer */}
      <Drawer open={cartDrawerOpen} onOpenChange={setCartDrawerOpen}>
        <DrawerContent className="max-h-[85vh]">
          <DrawerHeader className="border-b">
            <div className="grid grid-cols-[40px_1fr_40px] items-center">
              <ShoppingCart className="h-5 w-5 justify-self-start" aria-hidden="true" />
              <div className="text-center">
                <DrawerTitle>
                  {t("common.cart")}
                  {cart.selectedProducts.length > 0 && (
                    <span className="font-normal text-muted-foreground text-sm ml-1">
                      ({cartSummary.totalItems} {cartSummary.totalItems === 1 ? "item" : "items"})
                    </span>
                  )}
                </DrawerTitle>
                <DrawerDescription>
                  {cartSummary.totalValueCents > 0
                    ? `$${formatCentsAsDollars(cartSummary.totalValueCents)}${cartSummary.hasMissingPrices ? "+" : ""}`
                    : t("components.orders.newOrderFlow.reviewYourSelectedProducts")}
                </DrawerDescription>
              </div>
              <DrawerClose asChild>
                <Button variant="ghost" size="icon" className="justify-self-end h-8 w-8">
                  <X className="h-4 w-4" />
                </Button>
              </DrawerClose>
            </div>
          </DrawerHeader>
          <div className="overflow-y-auto flex-1 p-4">
            <CartItemList
              selectedProducts={cartProductSelections}
              onQuantityChange={cart.changeQuantity}
              onRemove={handleRemoveProduct}
              onClearAll={cart.clearAll}
              showPricing
            />
          </div>
          <div className="border-t p-4 space-y-2">
            <Button
              onClick={() => { setCartDrawerOpen(false); handleGoToCheckout(); }}
              disabled={cart.selectedProducts.length === 0}
              className="w-full"
            >
              {t("components.orders.newOrderFlow.checkOutRarr")}
            </Button>
          </div>
        </DrawerContent>
      </Drawer>

      {/* Save-as-template dialog */}
      <SaveTemplateDialog
        open={templateMgmt.showSaveTemplateDialog}
        onOpenChange={templateMgmt.handleSaveTemplateDialogClose}
        orderData={{
          title: "",
          description: "",
          fulfillmentType: "delivery",
          deliveryDate: "",
          notes: "",
          products: cart.selectedProducts,
        }}
        onSaveTemplate={templateMgmt.handleTemplateSaved}
      />

      {/* Template override confirmation */}
      <AlertDialog open={templateMgmt.showTemplateConfirm} onOpenChange={(open) => { if (!open) templateMgmt.cancelTemplateConfirm(); }}>
        <AlertDialogContent className="sm:max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("components.orders.newOrderFlow.replaceCart_2")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("components.orders.newOrderFlow.yourCartHas")} {cart.selectedProducts.length} {cart.selectedProducts.length === 1 ? "item" : "items"}{t("components.orders.newOrderFlow.loadingATemplateWill")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-end">
            <AlertDialogCancel onClick={templateMgmt.cancelTemplateConfirm}>
              {t("common.cancel")}
            </AlertDialogCancel>
            <Button variant="outline" onClick={templateMgmt.saveAndReplaceTemplate}>
              {t("components.orders.newOrderFlow.saveAsTemplateFirst")}
            </Button>
            <AlertDialogAction onClick={templateMgmt.confirmTemplateReplace}>
              {t("components.orders.newOrderFlow.replaceCart")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
};

/** Read-only order view for submitted orders. */
function ViewModeContent({
  editOrder,
  cartProductSelections,
  onBack,
}: {
  editOrder?: EditOrderData;
  cartProductSelections: ProductSelection[];
  onBack: () => void;
}) {
  const { t } = useTranslation();
  const [, navigate] = useLocation();

  return (
    <div className="flex-1 min-h-0 overflow-y-auto mt-4">
      <div className="flex flex-col gap-6 max-w-2xl mx-auto pb-4">
        {editOrder && (
          <div className="space-y-2">
            {editOrder.title && (
              <div className="text-sm">
                <span className="text-muted-foreground">{t("components.orders.newOrderFlow.poNumber")} </span>
                <span className="font-medium">{editOrder.title}</span>
              </div>
            )}
            <div className="text-sm">
              <span className="text-muted-foreground">{t("components.orders.newOrderFlow.fulfillment")} </span>
              <span className="font-medium capitalize">{editOrder.fulfillmentType}</span>
            </div>
            {editOrder.notes && (
              <div className="text-sm">
                <span className="text-muted-foreground">{t("components.orders.newOrderFlow.notes")} </span>
                <span>{editOrder.notes}</span>
              </div>
            )}
          </div>
        )}
        <OrderSummaryPanel selectedProducts={cartProductSelections} showPricing />
        <div className="flex gap-3">
          <Button variant="outline" onClick={onBack}>
            {t("components.orders.newOrderFlow.backToOrders")}
          </Button>
          {editOrder?.orderStatus === "draft" && (
            <Button onClick={() => navigate(`/orders/edit/${editOrder.id}`)}>
              {t("common.editOrder")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
